import React, { useEffect, useState } from 'react';
import { Pages, Panels } from '../Consts';
import { useAppContext } from '../../AppContext';
import {
  Button, Div, Panel,
  SimpleCell, Text, Title, PullToRefresh
} from '@vkontakte/vkui';
import { UserAvatar } from '../components/UserAvatar';
import { DISTRIBUTION_TYPE_EQUALLY, DISTRIBUTION_TYPE_PARTS, DISTRIBUTION_TYPE_VALUE } from './components/DistributionTypeTabsComponent';
import PropTypes from 'prop-types';
import { ErrorLoadComponent } from '../components/ErrorLoadComponent';
import { clearCache as eventInfoCacheClear } from '../Events/ShowEventPanel';
import { ImgsGallery } from '../components/ImgsGalleryComponent';
import { getRouteEventInfo, getRouteEditPayment } from '../../route';
import { PlatformPanelHeader } from '../components/PlatformPanelHeader';

const CURRENT_PANEL = Panels.PAYMENT_INFO_PANEL;

const ValueType = ({members}) => {
  const context = useAppContext();

  return (
    <>
      {
        members.map(({id, amount, type, img, name}) => {
          return (<SimpleCell
            disabled={true}
            key={id}
            subtitle={context.lang().formatPrice(amount)}
            before={<UserAvatar size={40} type={type} src={img} name={name} id={id} />}
          >
            {name}
          </SimpleCell>);
        })
      }
    </>
  );
}

const PartType = ({members}) => {
  const context = useAppContext();

  return (
    <>
      {
        members.map(({id, amount, type, img, name}) => {
          return (<SimpleCell
            disabled={true}
            key={id}
            subtitle={context.lang().formatPrice(amount)}
            before={<UserAvatar size={40} type={type} src={img} name={name} id={id} />}
          >
            {name}
          </SimpleCell>);
        })
      }
    </>
  );
}

const EquallyType = ({members}) => {
  const context = useAppContext();

  return (
    <>
      {
        members.map(({id, amount, type, img, name}) => {
          return (<SimpleCell
            disabled={true}
            key={id}
            subtitle={context.lang().formatPrice(amount)}
            before={<UserAvatar size={40} type={type} src={img} name={name} id={id} />}
          >
            {name}
          </SimpleCell>);
        })
      }
    </>
  );
}

const ShowPaymentPanel = () => {
  const context = useAppContext();
  const params = context.router.getParams();

  const [item, setItem] = useState(null);
  const [users, setUsers] = useState([]);
  const [creator, setCreator] = useState({});
  const [errorInit, setErrorInit] = useState(null);
  const [isRefreshFetching, setIsRefreshFetching] = React.useState(false);

  const fetch = () => {
    context.spinner(true);
    context.init().then(() => {
      context.httpClient()
        .getPayment(params.eventId, params.id)
        .then((response) => {
          const users = {};
          response.data.users.forEach((user) => {
            users[user.id] = user;
          });

          setCreator(users[response.data.item.creator_id]);
          setUsers(response.data.item.members.map((member) => ({ ...member, ...users[member.id] })));
          setItem(response.data.item);

          setErrorInit(null);
          context.spinner(false);
        })
        .catch((error) => {
          setErrorInit(error);
        });
    })
    .catch((error) => {
      setErrorInit(error);
    });
  }

  const onRefresh = () => {
    setIsRefreshFetching(true);
    context.httpClient()
      .getPayment(params.eventId, params.id)
      .then((response) => {
        const users = {};
        response.data.users.forEach((user) => {
          users[user.id] = user;
        });

        setCreator(users[response.data.item.creator_id]);
        setUsers(response.data.item.members.map((member) => ({ ...member, ...users[member.id] })));
        setItem(response.data.item);

        setErrorInit(null);
        setIsRefreshFetching(false);
      })
      .catch((error) => {
        setErrorInit(error);
        setIsRefreshFetching(false);
      });
  }

  useEffect(() => {
    fetch();
  }, []);

  if (errorInit) {
    return (<ErrorLoadComponent
      error={errorInit}
      onFetch={fetch}
    />)
  }

  if (item === null) {
    return (<Panel id={CURRENT_PANEL}></Panel>);
  }

  const deleteHandle = () => {
    context.confirm(
      context.lang().getKey('app', 'confirm_delete_message'),
      (value) => {
        if (!value) {
          return;
        }

        context.httpClient()
          .deletePaymentV1(params.eventId, params.id)
          .then(() => {
            eventInfoCacheClear(params.eventId, context.cache);
            context.router.backTo(Panels.EVENT_INFO)
              .then(() => {})
              .catch((r) => r.replace(...getRouteEventInfo(params.eventId)));
          })
          .catch((error) => context.showError(error));
      },
      context.lang().getKey('app', 'confirm_delete_ok_label'),
      context.lang().getKey('app', 'confirm_delete_cancel_label')
    );
  }

  const editHandle = () => {
    context.router.push(...getRouteEditPayment(params.id, params.eventId));
  }

  const { name, comment, images, date } = item;

  return (<Panel id={CURRENT_PANEL}>
    <PlatformPanelHeader
      text={name}
    />
    {context.snackbar}
    <PullToRefresh onRefresh={() => onRefresh()} isFetching={isRefreshFetching}>
      <Div>
        <Title level="2" weight="1">{name}</Title>
        {
          comment.length > 0 && (<Text weight="2">{comment}</Text>)
        }
        <Text weight="2" >{context.lang().dateFormatLong(new Date(date))}</Text>
      </Div>

      <SimpleCell
        disabled={true}
        subtitle={context.lang().getKey(CURRENT_PANEL, 'payment_man_description', '', {amount: context.lang().formatPrice(item.amount)})}
        before={<UserAvatar src={creator.img} type={creator.type} name={creator.name} id={creator.id} />}
      >
        {creator.name}
      </SimpleCell>
      <Div className={'ShowPayment__distributionType'}>
        {
          item.distribution_type === DISTRIBUTION_TYPE_EQUALLY ? (<EquallyType members={users} />) : undefined
        }
        {
          item.distribution_type === DISTRIBUTION_TYPE_PARTS ? (<PartType members={users} />) : undefined
        }
        {
          item.distribution_type === DISTRIBUTION_TYPE_VALUE ? (<ValueType members={users} />) : undefined
        }
      </Div>
      <Div>
        <ImgsGallery images={images} />
      </Div>
    </PullToRefresh>
    <Div>
      <Button
        stretched
        size="l"
        mode="outline"
        appearance="negative"
        onClick={() => deleteHandle()}
      >
        {context.lang().getKey('app', 'delete_button_label')}
      </Button>
    </Div>
    <Div>
      <Button
        stretched
        size="l"
        onClick={() => editHandle()}
      >
        {context.lang().getKey('app', 'edit_button_label')}
      </Button>
    </Div>
  </Panel>);
}

export default ShowPaymentPanel;

const members = PropTypes.shape({
  amount: PropTypes.number.isRequired,
  id: PropTypes.number.isRequired,
});

const users = PropTypes.shape({
  id: PropTypes.number.isRequired,
  img: PropTypes.string,
  name: PropTypes.string.isRequired,
  payment_info: PropTypes.string.isRequired,
  vk_id: PropTypes.number,
  tg_id: PropTypes.number,
});

ValueType.propTypes = {
  members: PropTypes.arrayOf(members.isRequired).isRequired,
};
PartType.propTypes = {
  members: PropTypes.arrayOf(members.isRequired).isRequired,
};
EquallyType.propTypes = {
  members: PropTypes.arrayOf(members.isRequired).isRequired,
};
